/** A browser that has received ten keeps ten, including after logout and
 * creating a new project. The server attaches this promise to the account.
 * This stores no project contents or analytics identity. */
const KEY = "twelve-volt:free-allowance-10:v1";

export function preserveFreeAllowance(): void {
  try { localStorage.setItem(KEY, "10"); } catch { /* Best effort; server receipts remain authoritative. */ }
}

export function hasPreservedFreeAllowance(): boolean {
  try { return localStorage.getItem(KEY) === "10"; } catch { return false; }
}

/** Called before first-touch creation. Otherwise an old local drawing with
 * no arrival record would look like a brand-new visitor to the server. */
export function preserveLegacyFreeAllowance(): void {
  try {
    const arrival = localStorage.getItem("twelve-volt:first-touch");
    if (arrival) {
      try { if (typeof JSON.parse(arrival)?.surface === "string") return; } catch { /* An invalid arrival is replaced at boot. */ }
    }
    if (localStorage.getItem("twelve-volt:system") !== null ||
      localStorage.getItem("twelve-volt:canvas-used-v1") !== null) preserveFreeAllowance();
  } catch { /* Storage can be unavailable. */ }
}

/** Covers older IndexedDB projects when their companion localStorage marker
 * is missing. Compare creation time with the first arrival, so a project made
 * during this visit cannot accidentally grandfather a new treatment user. */
export async function hasPriorFreeAllowance(firstSeenAt: unknown): Promise<boolean> {
  if (hasPreservedFreeAllowance()) return true;
  if (typeof indexedDB === "undefined" || typeof indexedDB.databases !== "function" ||
    typeof firstSeenAt !== "string" || !Number.isFinite(Date.parse(firstSeenAt))) return false;
  try {
    const databases = await indexedDB.databases();
    if (!databases.some((database) => database.name === "crimp-projects")) return false;
    const existed = await new Promise<boolean>((resolve) => {
      const request = indexedDB.open("crimp-projects");
      // If an existing database is unreadable, preserve the more generous
      // allowance rather than silently reducing an unknown prior promise.
      const timer = setTimeout(() => resolve(true), 1_000);
      const finish = (value: boolean) => { clearTimeout(timer); resolve(value); };
      request.onerror = () => finish(true);
      request.onupgradeneeded = () => { request.transaction?.abort(); finish(false); };
      request.onsuccess = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains("projects")) { database.close(); finish(false); return; }
        const transaction = database.transaction("projects", "readonly");
        const cursor = transaction.objectStore("projects").openCursor();
        let previous = false;
        cursor.onsuccess = () => {
          const row = cursor.result;
          if (!row) return;
          const createdAt = Date.parse(row.value?.createdAt);
          if (!Number.isFinite(createdAt) || createdAt < Date.parse(firstSeenAt)) previous = true;
          else row.continue();
        };
        transaction.oncomplete = () => { database.close(); finish(previous); };
        transaction.onerror = () => { database.close(); finish(true); };
        transaction.onabort = () => { database.close(); finish(true); };
      };
    });
    if (existed) preserveFreeAllowance();
    return existed;
  } catch { return false; }
}
